/**
 * Stock Chart Viewer - AI Assistant
 */

import { state } from '../state.js';
import { elements } from '../ui/dom.js';

// 空文字列の場合は相対パス（同一オリジン）を使用
const API_URL = import.meta.env.VITE_API_URL ?? '';

// Chat history
let chatHistory = [];

/**
 * Initialize AI assistant UI
 */
export function initAIAssistant() {
    if (!elements.aiChatInput || !elements.aiSendBtn) {
        console.log('AI Assistant elements not found');
        return;
    }

    // Toggle button to open panel
    if (elements.aiToggleBtn) {
        elements.aiToggleBtn.addEventListener('click', toggleAIPanel);
    }

    // Close button to close panel
    if (elements.aiCloseBtn) {
        elements.aiCloseBtn.addEventListener('click', closeAIPanel);
    }

    // Send button click
    elements.aiSendBtn.addEventListener('click', sendMessage);

    // Enter key to send
    elements.aiChatInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    });

    // Check AI status (only when panel is first opened)
    // We'll check on first open instead of on init
}

/**
 * Toggle AI panel visibility
 */
function toggleAIPanel() {
    const isHidden = elements.aiPanel.classList.contains('hidden');
    if (isHidden) {
        openAIPanel();
    } else {
        closeAIPanel();
    }
}

/**
 * Open AI panel
 */
function openAIPanel() {
    elements.aiPanel.classList.remove('hidden');
    elements.aiToggleBtn.classList.add('hidden');
    // Check AI status on first open
    if (!window._aiStatusChecked) {
        checkAIStatus();
        window._aiStatusChecked = true;
    }
}

/**
 * Close AI panel
 */
function closeAIPanel() {
    elements.aiPanel.classList.add('hidden');
    elements.aiToggleBtn.classList.remove('hidden');
}

/**
 * Check if AI is available
 */
async function checkAIStatus() {
    try {
        const response = await fetch(`${API_URL}/api/ai/status`);
        const data = await response.json();

        if (data.available) {
            addSystemMessage('AIアシスタントが利用可能です。株式について質問してください。');
        } else {
            addSystemMessage('AIアシスタントは現在利用できません（APIキー未設定）');
        }
    } catch (error) {
        addSystemMessage('AIサーバーに接続できません');
    }
}

/**
 * Send message to AI
 */
async function sendMessage() {
    const message = elements.aiChatInput.value.trim();
    if (!message) return;

    // Add user message
    addMessage('user', message);
    elements.aiChatInput.value = '';

    // Show loading
    const loadingEl = addMessage('ai', '考え中...');
    loadingEl.classList.add('loading');

    try {
        // Build context from current stock
        const context = state.stockData ? {
            symbol: state.stockData.symbol,
            company: state.stockData.company,
            price: state.stockData.currentPrice,
            change: state.stockData.change,
            changePercent: state.stockData.changePercent,
            period: state.currentPeriod
        } : null;

        const response = await fetch(`${API_URL}/api/ai/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message, context })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        // Replace loading message
        loadingEl.classList.remove('loading');
        loadingEl.innerHTML = formatAIResponse(data.response);

    } catch (error) {
        loadingEl.classList.remove('loading');
        loadingEl.textContent = `エラー: ${error.message}`;
        loadingEl.classList.add('error');
    }
}

/**
 * Add message to chat
 */
function addMessage(type, content) {
    const messagesEl = elements.aiChatMessages;
    if (!messagesEl) return null;

    const messageEl = document.createElement('div');
    messageEl.className = `chat-message ${type}`;
    messageEl.innerHTML = `
        <span class="avatar">${type === 'user' ? '👤' : '🤖'}</span>
        <div class="content">${content}</div>
    `;

    messagesEl.appendChild(messageEl);
    messagesEl.scrollTop = messagesEl.scrollHeight;

    chatHistory.push({ type, content });

    return messageEl.querySelector('.content');
}

/**
 * Add system message
 */
function addSystemMessage(content) {
    const messagesEl = elements.aiChatMessages;
    if (!messagesEl) return;

    const messageEl = document.createElement('div');
    messageEl.className = 'chat-message system';
    messageEl.innerHTML = `<div class="content">${content}</div>`;
    messagesEl.appendChild(messageEl);
}

/**
 * Format AI response (basic markdown)
 */
function formatAIResponse(text) {
    return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>')
        .replace(/^- /gm, '• ');
}

/**
 * Quick analysis of current stock
 */
export async function analyzeCurrentStock() {
    if (!state.stockData) {
        addSystemMessage('分析する銘柄を選択してください');
        return;
    }

    elements.aiChatInput.value = `${state.stockData.symbol}（${state.stockData.company}）を分析してください。`;
    await sendMessage();
}
